<script lang="ts">
  import { createEventDispatcher, getContext } from "svelte";
  import { createPopperActions } from "svelte-popperjs";
  import { get, type Writable } from "svelte/store";
  import { categoryURL, docURL } from "$utils/urls";
  import { slugify } from "$utils/strings";
  import { communitySlug } from "$stores/community";
  import { portal } from "$actions/portal.svelte";
  import { clickOutside } from "$actions/clickOutside";
  import { docsKey, categoriesKey } from "./Gallery.svelte";
  import Dropdown from "$components/+blocks/Dropdown/Dropdown.svelte";
  import Search from "$components/+blocks/Dropdown/fields/Search.svelte";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import OptionGroup from "$components/+blocks/Dropdown/fields/OptionGroup.svelte";
  import Option from "$components/+blocks/Dropdown/fields/Option.svelte";
  import InlineTextArea from "$components/+blocks/InlineTextArea/InlineTextArea.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Heading from "$components/+blocks/Dropdown/fields/Heading.svelte";
  import Button from "$components/+blocks/Button/Button.svelte";
  import IconButton from "$components/+blocks/IconButton/IconButton.svelte";
  import cx from "classnames";
  import { fade, fly } from "svelte/transition";
  import { sineOut } from "svelte/easing";
  import type { Item } from "./DocsGalleryBlock";
  import type { Document, Folder, WritableFolder } from "app/models/documents";
  import { getStores } from "$stores";
  import { Trash, Category, Doc, Down } from "$components/+blocks/OVIcon";
  import { onlyDocs } from "app/stores/documents";
  import { isDarkTheme } from "app/stores/userPreferences";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import type { WritableDocument } from "app/models/documents";

  export let item: Item;
  export let isEditable = false;
  export let isHovering = false;

  const { docs } = getStores();

  let selectedItem: Document | Folder | undefined = undefined;
  let isDropdownOpen = false;
  let isDescriptionOpen = false;
  let description: HTMLButtonElement;
  let url = "";
  const dispatch = createEventDispatcher();
  const documents = getContext<Writable<Document[]>>(docsKey);
  const categories = getContext<Writable<Folder[]>>(categoriesKey);
  const [popperRef, popperContent] = createPopperActions({
    placement: "bottom-start",
    strategy: "absolute",
  });
  const extraOpts = {
    modifiers: [
      {
        name: "offset",
        options: { offset: [-12, 0] }, // offset the dropdown to the left and up to align with the trigger
      },
    ],
  };

  // informing the row (its parent) that this item is being edited
  $: dispatch("editing-item", isDropdownOpen || isDescriptionOpen);

  let currentDocument: WritableDocument;
  let currentFolder: WritableFolder;
  let customIcon: string | undefined;
  $: $isDarkTheme, getCustomIcon(item);

  const getDocIcon = (docUuid: string) => {
    currentDocument = docs.getDocumentSynchronously(docUuid);

    // Later work can be done to handle gracefully handle deleted content
    if (!currentDocument) {
      return $isDarkTheme ? item.iconDark : item.icon;
    }

    //  light mode icon is required, if not present no icons should render
    if (!$currentDocument.icon) {
      return undefined;
    }

    if (!$isDarkTheme) {
      return $currentDocument.icon;
    }

    // default to light mode icon if dark mode icon is not provided
    return $currentDocument.iconDark
      ? $currentDocument.iconDark
      : ($currentDocument.icon ?? undefined);
  };

  const getFolderIcon = (folderUuid: string) => {
    currentFolder = docs.getFolder(folderUuid);

    // Later work can be done to handle gracefully handle deleted content
    if (!currentFolder) {
      return $isDarkTheme ? item.iconDark : item.icon;
    }

    // light mode icon is required, if not present no icons should render
    if (!$currentFolder.icon) {
      return undefined;
    }

    if (!$isDarkTheme) {
      return $currentFolder.icon;
    }

    // default to light mode icon if dark mode icon is not provided
    return $currentFolder.iconDark ? $currentFolder.iconDark : ($currentFolder.icon ?? undefined);
  };

  const getItemTitle = (item: Item) => {
    if (item.type === "folder") {
      currentFolder = docs.getFolder(item.uuid);

      // Later work can be done to handle gracefully handle deleted content
      if (!currentFolder) {
        return item.title;
      }
      return $currentFolder.title;
    } else {
      currentDocument = docs.getDocumentSynchronously(item.uuid);

      // Later work can be done to handle gracefully handle deleted content
      if (!currentDocument) {
        return item.title;
      }
      return $currentDocument.title;
    }
  };

  const getCustomIcon = (item: Item) => {
    if (item.type === "doc") {
      customIcon = getDocIcon(item.uuid);
    } else {
      customIcon = getFolderIcon(item.uuid);
    }
  };

  $: {
    // for folders use first doc as url (when loaded)
    let doc;
    if (item.type === "folder") {
      url = categoryURL({
        categoryUuid: item.uuid,
        categorySlug: slugify(item.title),
        communitySlug: $communitySlug,
      });
    } else {
      // ensure we get the most up to date doc to generate the url to account for title changes
      doc = get(docs.getDocumentSynchronously(item.uuid));
    }

    if (doc) {
      url = docURL({
        docUuid: doc.uuid,
        docSlug: slugify(doc.title),
        communitySlug: $communitySlug,
      });
    }
  }

  function isFolder(item: Document | Folder): item is Folder {
    return "items" in item;
  }
</script>

<a
  class={cx(
    "group-one in-out-shadow flex h-full flex-col justify-between rounded-lg bg-black/2 pt-5 transition after:rounded-lg dark:bg-white/3",
    {
      "hover:bg-white dark:hover:bg-white/5": !isEditable,
    }
  )}
  href={isEditable ? undefined : url}
  on:mouseover={() => (isHovering = true)}
  on:mouseleave={() => (isHovering = false)}
  on:focus={() => (isHovering = true)}
  on:blur={() => (isHovering = false)}
  tabindex={0}
>
  <div class="relative flex h-full flex-col gap-4 overflow-hidden px-5">
    <div class="flex w-full items-start justify-between gap-3 overflow-hidden">
      <div
        class="flex items-center justify-center rounded-[5px] border border-black/8 bg-white p-1.5 dark:border-white/5 dark:bg-white/5"
      >
        {#if customIcon}
          <Avatar mainImage={customIcon} radius="rounded-[3px]" />
        {:else}
          <OVIcon
            icon={item.type === "doc" ? Doc : Category}
            class="text-gray-500/80 dark:text-gray-400"
          />
        {/if}
      </div>

      {#if isEditable && isHovering}
        <div class="animate-fade-in-short">
          <IconButton
            a11yLabel="Remove item"
            icon={Trash}
            variant="tertiary"
            on:click={() => dispatch("delete-item", item.cellUUID)}
          />
        </div>
      {/if}

      {#if item.type === "folder" && (!isHovering || !isEditable)}
        <span
          class="border-1.5 shrink-0 rounded-full border border-black/8 bg-gray-50 px-2 py-0.5 text-xs text-gray-500 shadow-sm dark:border-white/5 dark:bg-white/8 dark:text-gray-400"
        >
          {item.count} article{item.count && item.count > 1 ? "s" : ""}
        </span>
      {/if}
    </div>

    {#if isEditable}
      <Dropdown bind:open={isDropdownOpen}>
        <Button
          let:active
          slot="trigger"
          variant={active ? "active" : "secondary"}
          label={item.title.slice(0, 20) + (item.title.length > 20 ? "..." : "")}
          iconRight={Down}
        />

        <Search placeholder="Search categories & docs..." />
        <Separator class="w-full" />

        <Heading title={"Docs"} />
        <OptionGroup
          maxHeight="250px"
          bind:value={selectedItem}
          on:select={() =>
            dispatch("update-item", {
              cellUUID: item.cellUUID,
              title: selectedItem?.title,
              uuid: selectedItem?.uuid,
              icon: selectedItem?.icon,
              iconDark: selectedItem?.iconDark ? selectedItem.iconDark : selectedItem?.icon,
              type: "doc",
              description: item.description,
            })}
        >
          {#each $documents as doc}
            <Option
              title={doc?.title}
              description={doc?.folder?.title}
              isDescriptionInline
              icon={Doc}
              customIconUrl={getDocIcon(doc.uuid)}
              value={doc}
              key={(d) => d.uuid}
            />
          {/each}
        </OptionGroup>

        <Separator class="mt-2 w-full" />

        <Heading title={"Categories"} />
        <OptionGroup
          maxHeight="250px"
          bind:value={selectedItem}
          on:select={() => {
            if (selectedItem && isFolder(selectedItem)) {
              dispatch("update-item", {
                cellUUID: item.cellUUID,
                title: selectedItem?.title,
                uuid: selectedItem?.uuid,
                type: "folder",
                icon: selectedItem?.icon,
                iconDark: selectedItem?.iconDark ? selectedItem.iconDark : selectedItem?.icon,
                description: item.description,
                count: onlyDocs(selectedItem?.items).length,
              });
            }
          }}
        >
          {#each $categories as category}
            <Option
              title={category.title}
              icon={Category}
              customIconUrl={getFolderIcon(category.uuid)}
              value={category}
              key={(f) => f.uuid}
            />
          {/each}
        </OptionGroup>
      </Dropdown>
    {:else}
      <h3 class="mb-1 shrink-0 truncate font-medium tracking-wide text-gray-500 dark:text-gray-200">
        {getItemTitle(item)}
      </h3>
    {/if}
  </div>

  <div class="h-full px-2 pb-2">
    {#if isEditable}
      <button
        class={cx("flex h-full w-full rounded px-3 transition-colors duration-[50ms]", {
          "hover:!bg-opacity-100 hover:glossy-effect": !isDescriptionOpen && isEditable,
        })}
        on:click={() => (isDescriptionOpen = true)}
        bind:this={description}
        type="button"
      >
        <span use:popperRef />
        <!-- hack to get popper to link to the top of the description section -->
        <p
          class="mt-1 line-clamp-3 text-left text-[13px] leading-6 font-normal text-gray-500 dark:text-gray-400"
        >
          {#if !isDescriptionOpen}
            {#if !item.description}
              <div class="text-gray-400 dark:text-gray-500">Add description</div>
            {:else}
              {item.description}
            {/if}
          {/if}
        </p>
      </button>
    {:else}
      <div class="h-full w-full overflow-hidden px-3 text-[13px]">
        <p
          class="line-clamp-3 text-left text-[13px] leading-6 font-normal text-gray-500/80 dark:text-gray-400/80"
        >
          {item.description}
        </p>
      </div>
    {/if}

    {#if isDescriptionOpen}
      <div
        use:popperContent={extraOpts}
        use:clickOutside={() => (isDescriptionOpen = false)}
        use:portal={{ target: "body", hasBackLayer: true }}
        class="flex w-full animate-light-mode-shadow rounded-[3px] glossy-effect font-normal dark:animate-dark-mode-shadow"
        style={`max-width: ${description?.getBoundingClientRect().width}px; min-height: ${
          description?.getBoundingClientRect().height
        }px`}
        out:fade={{ duration: 85, easing: sineOut }}
      >
        <div class="w-full" transition:fly={{ y: -8, opacity: 1, duration: 110, easing: sineOut }}>
          <InlineTextArea
            placeholder="Add description"
            textAlign="text-left"
            focusOnMount
            value={item.description}
            on:update={(event) =>
              dispatch("update-item", {
                ...item,
                description: event.detail.value,
              })}
          />
        </div>
      </div>
    {/if}
  </div>
</a>
